import React, { FC } from 'react';

import { Money as MoneyModel, PrimaryMarketProject, Reservation, StatusKey } from 'api-client';
import { CustomLink, Money, TertiaryButton } from 'components/common';
import { TertiaryButtonColor } from 'components/common/buttons/decorators/TertiaryButton/TertiaryButton';
import { CheckIcon } from 'components/common/icons';
import { ProjectDebtClass } from 'components/project';
import { formatDate } from 'formatters';
import { useProjectRowClick } from 'hooks';
import { RouteDictionary } from 'utils';

import { ymAsync } from '../../../../../utils/functions/ymWrapper';
import { usePrimaryMarketProjectContent } from '../usePrimaryMarketProjectContent';
import styles from './PrimaryMarketMobileCard.module.scss';

interface Props {
  project: PrimaryMarketProject;
  statusKey: StatusKey;
  reservation?: Reservation;
}

const PrimaryMarketMobileCard: FC<Props> = ({ project, statusKey, reservation }) => {
  const {
    devAndTest,
    showInvestButton,
    isFullyCollected,
    roiIrr,
    progressBar,
    term,
    factTerm,
    plannedCompletion,
    startedAt,
    completedAt,
    cancelledAt,
  } = usePrimaryMarketProjectContent(project);
  const isPaidTable = statusKey === 'Завершен';
  const isCurrentTable = statusKey === 'Текущий';
  const isRefundedTable = statusKey === 'Отменен';

  const handleCardClick = useProjectRowClick(project.uuid, () => ymAsync('click-to-project-card'));

  const handleActionClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <article className={styles.card} onClick={handleCardClick}>
      <div className={styles.header}>
        <CustomLink
          to={RouteDictionary.DETAILED_PROJECT}
          params={{ uuid: project.uuid }}
          className={styles.projectLink}
          onClick={handleActionClick}
        >
          <ProjectDebtClass debtClass={project.debtClass} />
          <div className={styles.projectName}>{project.name[0].text}</div>
        </CustomLink>
      </div>

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Сумма сбора</span>
          <span className={styles.metricValue}>
            <Money money={project.debtPrice} compact />
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Прогноз взыскания</span>
          <span className={styles.metricValue}>
            {project.recoveryForecast ? <Money money={project.recoveryForecast} compact /> : '—'}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>ROI / IRR</span>
          <span className={styles.metricValue}>{roiIrr}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>{isPaidTable ? 'Факт. срок' : 'Срок'}</span>
          <span className={styles.metricValue}>{isPaidTable ? factTerm : term}</span>
        </div>
        {isCurrentTable && (
          <>
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Старт проекта</span>
              <span className={styles.metricValue}>{startedAt}</span>
            </div>
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Плановое завершение</span>
              <span className={styles.metricValue}>{plannedCompletion}</span>
            </div>
          </>
        )}
        {isPaidTable && (
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Дата завершения</span>
            <span className={styles.metricValue}>{completedAt}</span>
          </div>
        )}
        {isRefundedTable && (
          <>
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Старт проекта</span>
              <span className={styles.metricValue}>{startedAt}</span>
            </div>
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Дата отмены</span>
              <span className={styles.metricValue}>{cancelledAt}</span>
            </div>
          </>
        )}
      </div>

      <div className={styles.progress}>{progressBar}</div>

      {reservation && (
        <div className={styles.reservation}>
          <CheckIcon preset="circle-success" size={20} title="Резервирование" />
          <span className={styles.reservationLabel}>
            Создано резервирование на сумму{' '}
            <Money
              money={
                new MoneyModel(Number(reservation.money.amount), reservation.money.currencyCode)
              }
              compact
              withPennies={false}
            />
          </span>
        </div>
      )}

      {!reservation && showInvestButton && (
        <div className={styles.action} onClick={handleActionClick}>
          <TertiaryButton
            expanded
            color={TertiaryButtonColor.GREEN}
            className={styles.investButton}
          >
            <CustomLink
              className={styles.investLink}
              to={RouteDictionary.DETAILED_PROJECT}
              params={{ uuid: project.uuid }}
              onClick={() => ymAsync('invest-all-projects-button')}
            >
              {devAndTest ? 'Подать заявку' : 'Инвестировать'}
            </CustomLink>
          </TertiaryButton>
          <span className={styles.actionHint}>
            Сбор до {project.maturityDate ? formatDate(project.maturityDate) : '—'}
          </span>
        </div>
      )}

      {!reservation && !showInvestButton && isFullyCollected && (
        <div className={styles.collected}>
          <CheckIcon preset="circle-success" size={20} title="Собран" />
          <span className={styles.collectedLabel}>Собран</span>
        </div>
      )}
    </article>
  );
};

export default PrimaryMarketMobileCard;
